import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import classnames from 'classnames';
import { get, noop } from 'lodash';

import { ICONS } from 'Components/utils/icon/icons';

import { Icon } from 'Components/frameworks/icon/Icon.jsx';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink.jsx';
import responsiveness from 'Components/utils/responsive/Responsive.jsx';
import { getInstalmentPrice } from 'Components/utils/price/price.util';
import ReactAppContext from 'Context/ReactAppContext';
import { formatAmountForLocale } from 'Utils/currency-config-for-locale/currencyConfigForLocale';
import { getKeyValue } from 'Src/i18n';

const propTypes = {
  number: PropTypes.number,
  offer: PropTypes.string,
  offerBackgroundColor: PropTypes.string,
  canGoToBooking: PropTypes.bool,
  statusMessage: PropTypes.string,
  from: PropTypes.string,
  price: PropTypes.string,
  oldPrice: PropTypes.string,
  isCurrentMonth: PropTypes.bool,
  highlighted: PropTypes.bool,
  passivelyHighlighted: PropTypes.bool,
  highlightedDayIndex: PropTypes.number,
  onClick: PropTypes.func,
  children: PropTypes.node,
  travelAdvisorConfig: PropTypes.shape({
    href: PropTypes.string,
  }),
  popInDelay: PropTypes.number,
};

const defaultProps = {
  onClick: noop,
  onMouseEnter: noop,
  popInDelay: 300,
};

const LONG_PRICE_LENGTH = 8;
const EXTRA_LONG_PRICE_LENGTH = 11;

export class PriceCalendarDayContent extends React.Component<Props> {
  static contextType = ReactAppContext;
  constructor(props) {
    super(props);

    this.hoverTimeout = 0;

    this.state = {
      shouldRenderPopin: false,
    };
  }

  componentWillUnmount() {
    clearTimeout(this.hoverTimeout);
  }

  onDayEnter = () => {
    this.hoverTimeout = setTimeout(() => {
      this.setState({
        shouldRenderPopin: true,
      });
    }, this.props.popInDelay);
  };

  onDayLeave = () => {
    clearTimeout(this.hoverTimeout);
    this.setState({
      shouldRenderPopin: false,
    });
  };

  isASelectedDay() {
    return this.props.highlightedDayIndex !== -1;
  }

  isDesktop() {
    return get(this.props, 'responsive.isBrowserGreaterThan.S');
  }

  renderStatusMessage() {
    const { statusMessage, highlighted } = this.props;

    return statusMessage && !this.isASelectedDay() ? (
      <span
        className={classnames('PriceCalendarDayContent-statusMessage', {
          'PriceCalendarDayContent-statusMessage--highlighted': highlighted,
        })}
      >
        {statusMessage}
      </span>
    ) : null;
  }

  renderCallCenterLink() {
    const { statusMessage, canGoToBooking, passivelyHighlighted } = this.props;

    const isAPhoneCall =
      statusMessage === get(this.props, 'bookingConfig.priceCalendar.priceStatus.noDeparture', '');

    return isAPhoneCall && !canGoToBooking && !passivelyHighlighted ? (
      <Icon color="gray-darker" className="align-top" name={ICONS.PHONE} width="0.875rem" />
    ) : null;
  }

  renderMessage() {
    return <div>{this.isDesktop() ? this.renderStatusMessage() : this.renderCallCenterLink()}</div>;
  }

  renderFrom() {
    const { highlighted, from } = this.props;

    return this.isDesktop() ? (
      <span>
        <span
          className={classnames('PriceCalendarDayContent-from', {
            'PriceCalendarDayContent-from--translucent': !highlighted && this.isASelectedDay(),
          })}
        >
          {from}
        </span>
        <span> </span>
      </span>
    ) : null;
  }

  renderOldPrice() {
    const { price, oldPrice } = this.props;

    const displayInstalment = getKeyValue('common.price.displayInstalmentsPrice');

    return this.isDesktop() && oldPrice && oldPrice.formattedValue !== price?.formattedValue ? (
      <s className="PriceCalendarDayContent-oldPrice">
        {displayInstalment && ['pt-BR', 'es-AR'].includes(this.context.locale)
          ? formatAmountForLocale(
              Math.trunc(oldPrice.value),
              this.context.locale,
              oldPrice.currency,
              {
                precision: 0,
              },
            )
          : oldPrice.formattedValue}
      </s>
    ) : null;
  }

  renderPriceValue() {
    const { price, highlighted } = this.props;

    return (
      <span
        className={classnames(
          'PriceCalendarDayContent-price',
          { 'PriceCalendarDayContent-price--translucent': !highlighted && this.isASelectedDay() },
          { 'PriceCalendarDayContent-price--long': price.length >= LONG_PRICE_LENGTH },
          { 'PriceCalendarDayContent-price--extraLong': price.length >= EXTRA_LONG_PRICE_LENGTH },
        )}
      >
        {getInstalmentPrice(price, this.context.locale)}
        {this.renderOldPrice()}
      </span>
    );
  }

  renderPriceBlock() {
    const { price, passivelyHighlighted } = this.props;

    return price && !passivelyHighlighted ? (
      <div>
        {this.renderFrom()}
        {this.renderPriceValue()}
      </div>
    ) : null;
  }

  renderOfferBanner() {
    const { statusMessage, isCurrentMonth, offer, offerBackgroundColor } = this.props;

    const shouldRenderOffer = this.isDesktop() && isCurrentMonth;

    return !statusMessage && !this.isASelectedDay() ? (
      <span className="PriceCalendarDayContent-offer">
        {shouldRenderOffer && (
          <div
            className={classnames(
              'PriceCalendarDayContent-offerInner',
              `PriceCalendarDayContent-offerInner--${offerBackgroundColor}`,
              {
                'bg-booking': offerBackgroundColor === 'bestPrice',
              },
            )}
          >
            {offer}
          </div>
        )}
      </span>
    ) : null;
  }

  renderLastRoom() {
    const { isBestPrice } = this.props;

    return !isBestPrice ? <div className="PriceCalendarDayContent-lastRoom" /> : null;
  }

  renderOffer() {
    const { offer } = this.props;

    return offer ? (
      <div>
        {this.renderOfferBanner()}
        {this.renderLastRoom()}
      </div>
    ) : null;
  }

  renderContent() {
    const { canGoToBooking, number } = this.props;
    return (
      <Fragment>
        <span
          className={classnames('PriceCalendarDayContent-number', {
            'text-gray-light': !canGoToBooking,
          })}
        >
          {number}
        </span>
        <span className="PriceCalendarDayContent-priceContainer">
          {this.renderMessage()}
          {this.renderPriceBlock()}
        </span>
        {this.renderOffer()}
      </Fragment>
    );
  }

  render() {
    const {
      canGoToBooking,
      highlighted,
      isBestPrice,
      isCurrentMonth,
      isLastHighlighted,
      onClick,
      passivelyHighlighted,
      price,
      statusMessage,
      travelAdvisorConfig,
    } = this.props;

    const shouldDisplayPhoneCallLink =
      !this.isDesktop() &&
      statusMessage ===
        get(this.props, 'bookingConfig.priceCalendar.priceStatus.noDeparture', '') &&
      !canGoToBooking &&
      !passivelyHighlighted;

    return (
      <div className="PriceCalendarDayContent">
        {highlighted && (
          <div className="PriceCalendarDayContent-chevron PriceCalendarDayContent-chevron--start" />
        )}
        {isLastHighlighted && (
          <div className="PriceCalendarDayContent-chevron PriceCalendarDayContent-chevron--end" />
        )}
        <button
          className={classnames(
            'PriceCalendarDayContent-container',
            { 'PriceCalendarDayContent-container--open': canGoToBooking },
            {
              'PriceCalendarDayContent-container--highlighted': highlighted || passivelyHighlighted,
            },
            { 'PriceCalendarDayContent-container--cantBeBooked': !canGoToBooking },
            { 'PriceCalendarDayContent-container--callCenter': canGoToBooking && statusMessage },
            { 'PriceCalendarDayContent-container--notCurrentMonth': !isCurrentMonth },
            { 'PriceCalendarDayContent-container--bestPrice': isBestPrice },
            { 'text-booking': isBestPrice },
          )}
          type="button"
          onClick={onClick}
          onMouseEnter={!this.isASelectedDay() ? this.onDayEnter : noop}
          onMouseLeave={this.onDayLeave}
        >
          {shouldDisplayPhoneCallLink ? (
            <DynamicLink className="w-full h-full" url={get(travelAdvisorConfig, 'href', '')}>
              <div className="PriceCalendarDayContent-innerContainer">{this.renderContent()}</div>
            </DynamicLink>
          ) : (
            <div className="PriceCalendarDayContent-innerContainer">{this.renderContent()}</div>
          )}
        </button>
        {this.state.shouldRenderPopin && price && this.props.children}
      </div>
    );
  }
}

PriceCalendarDayContent.propTypes = propTypes;
PriceCalendarDayContent.defaultProps = defaultProps;

export default responsiveness(PriceCalendarDayContent);
